import { useState } from "react";
import ReactDOM from "react-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import MathContent from "../../../../../customComponents/MathContent";
import { useGetSchoolProfileQuery } from "../../../../settings/school/schoolApiSlice";

const LABELS = ["A", "B", "C", "D", "E", "F"];

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

// ── Shared school header ───────────────────────────────────────────────────────
const SchoolHeader = ({ school, borderColor = "#000", textColor = "#000" }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "14px",
      textAlign: "center",
      marginBottom: "10px",
    }}
  >
    {school?.logo && (
      <img
        src={school.logo}
        alt="school logo"
        style={{ width: "60px", height: "60px", objectFit: "contain", flexShrink: 0 }}
      />
    )}
    <div>
      {school?.name && (
        <div
          style={{
            fontSize: "18px",
            fontWeight: "bold",
            textTransform: "uppercase",
            color: textColor,
            letterSpacing: "0.5px",
          }}
        >
          {school.name}
        </div>
      )}
      {school?.motto && (
        <div style={{ fontSize: "11px", fontStyle: "italic", color: textColor, opacity: 0.75 }}>
          {school.motto}
        </div>
      )}
      {school?.address && (
        <div style={{ fontSize: "10px", color: textColor, opacity: 0.65 }}>
          {school.address}
        </div>
      )}
    </div>
  </div>
);

// ── Template 1: Classic ────────────────────────────────────────────────────────
const ClassicTemplate = ({ quiz, showAnswers, school }) => (
  <div
    style={{
      fontFamily: "'Times New Roman', Times, serif",
      padding: "40px 50px",
      color: "#000",
      background: "#fff",
    }}
  >
    <SchoolHeader school={school} />
    <div
      style={{
        textAlign: "center",
        borderBottom: "2px solid #000",
        paddingBottom: "12px",
        marginBottom: "16px",
      }}
    >
      <div
        style={{
          fontSize: "22px",
          fontWeight: "bold",
          textTransform: "uppercase",
          letterSpacing: "2px",
        }}
      >
        {quiz.subject}{" "}
        {(quiz.type || "Quiz").replace(/^\w/, (c) => c.toUpperCase())}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "40px",
          fontSize: "13px",
          marginTop: "8px",
        }}
      >
        <span>
          Class: <b>{quiz.level}</b>
        </span>
        <span>
          Duration: <b>{quiz.duration} mins</b>
        </span>
        <span>
          Date: <b>{fmtDate(quiz.due_at)}</b>
        </span>
        <span>
          Total Questions: <b>{quiz.questions?.length}</b>
        </span>
      </div>
    </div>

    <div
      style={{
        fontSize: "12px",
        marginBottom: "16px",
      }}
    >
      <div>
        Name: _________________________________________________________________
      </div>
    </div>

    {quiz.instruction && (
      <div
        style={{
          border: "1px solid #000",
          padding: "8px 12px",
          fontSize: "12px",
          marginBottom: "20px",
        }}
      >
        <b>Instructions:</b> <MathContent html={quiz.instruction} />
      </div>
    )}

    {quiz.questions?.map((q, i) => (
      <div
        key={q.id}
        style={{ marginBottom: "22px", pageBreakInside: "avoid" }}
      >
        <div
          style={{
            display: "flex",
            gap: "8px",
            fontSize: "14px",
            marginBottom: "8px",
          }}
        >
          <b style={{ minWidth: "22px" }}>{i + 1}.</b>
          <div>
            <MathContent html={q.body} />
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "5px 24px",
            paddingLeft: "30px",
          }}
        >
          {q.options?.map((opt, j) => (
            <div
              key={opt.id}
              style={{
                display: "flex",
                gap: "6px",
                fontSize: "13px",
                alignItems: "flex-start",
                fontWeight:
                  showAnswers && opt.is_answer ? "bold" : "normal",
                textDecoration:
                  showAnswers && opt.is_answer ? "underline" : "none",
              }}
            >
              <span style={{ minWidth: "22px" }}>({LABELS[j]})</span>
              <MathContent html={opt.body} />
            </div>
          ))}
        </div>
      </div>
    ))}

    {showAnswers && (
      <div
        style={{
          borderTop: "2px solid #000",
          marginTop: "32px",
          paddingTop: "14px",
        }}
      >
        <b style={{ fontSize: "13px", display: "block", marginBottom: "8px" }}>
          ANSWER KEY
        </b>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 20px",
            fontSize: "12px",
          }}
        >
          {quiz.questions?.map((q, i) => {
            const ci = q.options?.findIndex((o) => o.is_answer) ?? -1;
            return (
              <span key={q.id}>
                {i + 1}. {ci >= 0 ? LABELS[ci] : "—"}
              </span>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

// ── Template 2: Modern ─────────────────────────────────────────────────────────
const ModernTemplate = ({ quiz, showAnswers, school }) => (
  <div
    style={{
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
      background: "#fff",
      color: "#111",
    }}
  >
    <div
      style={{
        background: "#1d3557",
        color: "#fff",
        padding: "20px 40px",
      }}
    >
      {school?.logo || school?.name ? (
        <SchoolHeader school={school} textColor="#fff" />
      ) : null}
      <div
        style={{
          fontSize: "23px",
          fontWeight: "800",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {quiz.subject}{" "}
        {(quiz.type || "Quiz").replace(/^\w/, (c) => c.toUpperCase())}
      </div>
      <div
        style={{
          display: "flex",
          gap: "28px",
          fontSize: "12px",
          marginTop: "8px",
          opacity: "0.85",
        }}
      >
        <span>Class: {quiz.level}</span>
        <span>Duration: {quiz.duration} mins</span>
        <span>{fmtDate(quiz.due_at)}</span>
        <span>{quiz.questions?.length} Questions</span>
      </div>
    </div>

    <div
      style={{
        padding: "12px 40px",
        background: "#f0f4f8",
        borderBottom: "1px solid #ddd",
        fontSize: "12px",
      }}
    >
      Name: ________________________________________________________________
    </div>

    {quiz.instruction && (
      <div
        style={{
          margin: "16px 40px 0",
          padding: "10px 16px",
          background: "#fff8e1",
          borderLeft: "4px solid #f59e0b",
          fontSize: "12px",
        }}
      >
        <b>Instructions:</b> <MathContent html={quiz.instruction} />
      </div>
    )}

    <div style={{ padding: "16px 40px 40px" }}>
      {quiz.questions?.map((q, i) => (
        <div
          key={q.id}
          style={{ marginBottom: "24px", pageBreakInside: "avoid" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              marginBottom: "10px",
            }}
          >
            <div
              style={{
                background: "#1d3557",
                color: "#fff",
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                minWidth: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: "13px",
              }}
            >
              {i + 1}
            </div>
            <div style={{ fontSize: "14px", paddingTop: "4px" }}>
              <MathContent html={q.body} />
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px",
              paddingLeft: "40px",
            }}
          >
            {q.options?.map((opt, j) => (
              <div
                key={opt.id}
                style={{
                  border: `1px solid ${
                    showAnswers && opt.is_answer ? "#2e7d32" : "#cbd5e1"
                  }`,
                  borderRadius: "6px",
                  padding: "8px 12px",
                  display: "flex",
                  gap: "8px",
                  fontSize: "13px",
                  background:
                    showAnswers && opt.is_answer ? "#e8f5e9" : "#f8fafc",
                }}
              >
                <span
                  style={{
                    fontWeight: "700",
                    color: "#1d3557",
                    minWidth: "18px",
                  }}
                >
                  {LABELS[j]}
                </span>
                <MathContent html={opt.body} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    {showAnswers && (
      <div
        style={{
          margin: "0 40px 40px",
          borderTop: "2px solid #1d3557",
          paddingTop: "12px",
        }}
      >
        <b
          style={{
            fontSize: "13px",
            color: "#1d3557",
            display: "block",
            marginBottom: "8px",
          }}
        >
          ANSWER KEY
        </b>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 20px",
            fontSize: "12px",
          }}
        >
          {quiz.questions?.map((q, i) => {
            const ci = q.options?.findIndex((o) => o.is_answer) ?? -1;
            return (
              <span key={q.id}>
                <b>{i + 1}.</b> {ci >= 0 ? LABELS[ci] : "—"}
              </span>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

// ── Template 3: Compact (Nigerian School Exam Style) ──────────────────────────
const CompactTemplate = ({ quiz, showAnswers, school }) => (
  <div
    style={{
      fontFamily: "Arial, sans-serif",
      padding: "24px 40px",
      color: "#000",
      background: "#fff",
      fontSize: "13px",
    }}
  >
    <SchoolHeader school={school} />
    <div style={{ textAlign: "center", marginBottom: "6px" }}>
      <div
        style={{
          fontSize: "17px",
          fontWeight: "bold",
          textTransform: "uppercase",
        }}
      >
        {quiz.subject}{" "}
        {(quiz.type || "Examination").replace(/^\w/, (c) => c.toUpperCase())}
      </div>
      <div style={{ fontSize: "11px", marginTop: "4px" }}>
        Class: <b>{quiz.level}</b>&nbsp;|&nbsp; Duration:{" "}
        <b>{quiz.duration} mins</b>&nbsp;|&nbsp; Date:{" "}
        <b>{fmtDate(quiz.due_at)}</b>&nbsp;|&nbsp; Total:{" "}
        <b>{quiz.questions?.length} questions</b>
      </div>
    </div>

    <hr style={{ border: "1.5px solid #000", margin: "8px 0" }} />

    <div
      style={{
        fontSize: "11px",
        marginBottom: "6px",
      }}
    >
      Name: __________________________________________________________
    </div>

    <hr style={{ border: "0.5px solid #999", margin: "6px 0" }} />

    {quiz.instruction && (
      <div style={{ fontSize: "11px", marginBottom: "8px" }}>
        <b>Instructions:</b> <MathContent html={quiz.instruction} />
      </div>
    )}

    <hr style={{ border: "0.5px solid #999", margin: "6px 0 12px" }} />

    {quiz.questions?.map((q, i) => (
      <div
        key={q.id}
        style={{ marginBottom: "14px", pageBreakInside: "avoid" }}
      >
        <div style={{ display: "flex", gap: "6px", marginBottom: "4px" }}>
          <b style={{ minWidth: "20px" }}>{i + 1}.</b>
          <div style={{ fontSize: "13px" }}>
            <MathContent html={q.body} />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 28px",
            paddingLeft: "26px",
            fontSize: "12px",
          }}
        >
          {q.options?.map((opt, j) => (
            <span
              key={opt.id}
              style={{
                fontWeight: showAnswers && opt.is_answer ? "bold" : "normal",
                textDecoration:
                  showAnswers && opt.is_answer ? "underline" : "none",
                display: "inline-flex",
                gap: "3px",
                alignItems: "flex-start",
              }}
            >
              <b>{LABELS[j]}.</b>
              <MathContent html={opt.body} />
            </span>
          ))}
        </div>
      </div>
    ))}

    {showAnswers && (
      <>
        <hr style={{ border: "1px solid #000", marginTop: "16px" }} />
        <div style={{ marginTop: "8px" }}>
          <b style={{ fontSize: "12px" }}>ANSWERS: </b>
          <span style={{ fontSize: "11px" }}>
            {quiz.questions
              ?.map((q, i) => {
                const ci = q.options?.findIndex((o) => o.is_answer) ?? -1;
                return `${i + 1}${ci >= 0 ? LABELS[ci] : ""}`;
              })
              .join(",  ")}
          </span>
        </div>
      </>
    )}
  </div>
);

// ── Template thumbnails ────────────────────────────────────────────────────────
const TEMPLATES = [
  {
    id: "classic",
    label: "Classic",
    description: "Serif, traditional exam format",
    thumb: (
      <Box sx={{ p: 0.75, height: "100%", overflow: "hidden" }}>
        <Box sx={{ height: 5, bgcolor: "#222", width: "55%", mx: "auto", mb: 0.5 }} />
        <Box sx={{ height: 3, bgcolor: "#888", width: "35%", mx: "auto", mb: 0.5 }} />
        <Box sx={{ height: "1px", bgcolor: "#000", mb: 1 }} />
        <Box sx={{ height: 2, bgcolor: "#aaa", width: "70%", mb: 1 }} />
        {[1, 2, 3].map((k) => (
          <Box key={k} sx={{ mb: 0.75 }}>
            <Box sx={{ display: "flex", gap: 0.5, mb: 0.35, alignItems: "center" }}>
              <Box sx={{ width: 5, height: 3, bgcolor: "#444", flexShrink: 0 }} />
              <Box sx={{ height: 3, bgcolor: "#555", flex: 1 }} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.35, pl: 1 }}>
              {[1, 2, 3, 4].map((j) => (
                <Box key={j} sx={{ height: 3, bgcolor: "#bbb" }} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    ),
  },
  {
    id: "modern",
    label: "Modern",
    description: "Bold header, card options",
    thumb: (
      <Box sx={{ height: "100%", overflow: "hidden" }}>
        <Box sx={{ bgcolor: "#1d3557", height: 20, px: 0.75, py: 0.5 }}>
          <Box sx={{ height: 4, bgcolor: "rgba(255,255,255,0.9)", width: "60%", mb: 0.5 }} />
          <Box sx={{ height: 2.5, bgcolor: "rgba(255,255,255,0.5)", width: "80%" }} />
        </Box>
        <Box sx={{ bgcolor: "#f0f4f8", height: 8, px: 0.75, display: "flex", alignItems: "center" }}>
          <Box sx={{ height: 2, bgcolor: "#aaa", width: "75%" }} />
        </Box>
        <Box sx={{ p: 0.75 }}>
          {[1, 2].map((k) => (
            <Box key={k} sx={{ mb: 0.75 }}>
              <Box sx={{ display: "flex", gap: 0.5, mb: 0.35, alignItems: "center" }}>
                <Box sx={{ width: 7, height: 7, bgcolor: "#1d3557", borderRadius: "50%", flexShrink: 0 }} />
                <Box sx={{ height: 3, bgcolor: "#555", flex: 1 }} />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.35, pl: 1 }}>
                {[1, 2, 3, 4].map((j) => (
                  <Box key={j} sx={{ height: 5, bgcolor: "#e8eef4", border: "0.5px solid #c0cfe0", borderRadius: 0.5 }} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    ),
  },
  {
    id: "compact",
    label: "Compact",
    description: "Tight layout, inline options",
    thumb: (
      <Box sx={{ p: 0.75, height: "100%", overflow: "hidden" }}>
        <Box sx={{ height: 4, bgcolor: "#333", width: "50%", mx: "auto", mb: 0.25 }} />
        <Box sx={{ height: 2.5, bgcolor: "#888", width: "75%", mx: "auto", mb: 0.25 }} />
        <Box sx={{ height: "1.5px", bgcolor: "#000", mb: 0.5 }} />
        <Box sx={{ height: 2, bgcolor: "#aaa", width: "60%", mb: 0.5 }} />
        <Box sx={{ height: "0.75px", bgcolor: "#999", mb: 0.5 }} />
        {[1, 2, 3, 4].map((k) => (
          <Box key={k} sx={{ mb: 0.5 }}>
            <Box sx={{ display: "flex", gap: 0.5, mb: 0.2 }}>
              <Box sx={{ width: 5, height: 2.5, bgcolor: "#444", flexShrink: 0 }} />
              <Box sx={{ height: 2.5, bgcolor: "#555", flex: 1 }} />
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, pl: 0.75 }}>
              {[1, 2, 3, 4].map((j) => (
                <Box key={j} sx={{ height: 2.5, bgcolor: "#aaa", flex: 1 }} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    ),
  },
];

const TEMPLATE_COMPONENTS = {
  classic: ClassicTemplate,
  modern: ModernTemplate,
  compact: CompactTemplate,
};

// ── Main modal ─────────────────────────────────────────────────────────────────
const PrintQuizModal = ({ open, onClose, quiz }) => {
  const [template, setTemplate] = useState("classic");
  const [showAnswers, setShowAnswers] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { data: school } = useGetSchoolProfileQuery();

  const handlePrint = () => {
    const style = document.createElement("style");
    style.id = "__qpm_print_style__";
    style.textContent = `
      @media print {
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        body > *:not(#__qpm_print_portal__) { display: none !important; }
        #__qpm_print_portal__ {
          display: block !important;
          position: static !important;
          width: 100% !important;
        }
        @page { margin: 12mm 18mm; }
      }
    `;
    document.head.appendChild(style);
    window.print();
    setTimeout(() => document.getElementById("__qpm_print_style__")?.remove(), 1200);
  };

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    const el = document.getElementById("__qpm_print_portal__");
    // A4 at 96 dpi = 794 px; constrain width so content renders at A4-equivalent
    // size and doesn't get squished from a wide viewport.
    el.style.position = "absolute";
    el.style.left = "-9999px";
    el.style.width = "794px";
    el.style.display = "block";
    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false });
      el.style.display = "none";
      el.style.position = "";
      el.style.left = "";
      el.style.width = "";
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pageW) / canvas.width;
      let posY = 0;
      while (posY < imgH) {
        pdf.addImage(imgData, "JPEG", 0, -posY, pageW, imgH);
        posY += pageH;
        if (posY < imgH) pdf.addPage();
      }
      const filename = `${quiz?.subject || "quiz"}-${quiz?.type || "quiz"}`
        .replace(/\s+/g, "-")
        .toLowerCase() + ".pdf";
      pdf.save(filename);
    } finally {
      el.style.display = "none";
      setIsDownloading(false);
    }
  };

  const TemplateComponent = TEMPLATE_COMPONENTS[template];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "92vh", display: "flex", flexDirection: "column" } }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 1.5,
            borderBottom: "1px solid #eee",
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Print Quiz
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showAnswers}
                  onChange={(e) => setShowAnswers(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">Include answer key</Typography>
              }
              sx={{ mr: 0 }}
            />
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                color: "inherit",
                borderColor: "currentColor",
              }}
            >
              Print
            </Button>
            <Button
              variant="contained"
              startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              {isDownloading ? "Generating PDF…" : "Download"}
            </Button>
            <IconButton onClick={onClose} size="small" sx={{ color: "text.primary" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent
          sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 }}
        >
          {/* Template selector */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid #f0f0f0",
              bgcolor: "#fafafa",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ mb: 1.5, color: "#555" }}
            >
              Choose a template
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 130px)",
                gap: 1.5,
              }}
            >
              {TEMPLATES.map((t) => (
                <Box
                  key={t.id}
                  onClick={() => setTemplate(t.id)}
                  sx={{
                    cursor: "pointer",
                    border: template === t.id ? "2px solid" : "1px solid",
                    borderColor: template === t.id ? "primary.main" : "#ddd",
                    borderRadius: "10px",
                    overflow: "hidden",
                    bgcolor: template === t.id ? "#eff6ff" : "#fff",
                    transition: "border-color 0.15s, background-color 0.15s",
                    "&:hover": { borderColor: "primary.light" },
                  }}
                >
                  <Box sx={{ height: 100, overflow: "hidden", bgcolor: "#f8f8f8" }}>
                    {t.thumb}
                  </Box>
                  <Box
                    sx={{ px: 1.5, py: 0.75, borderTop: "1px solid #eee" }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      display="block"
                      sx={{ color: template === t.id ? "primary.main" : "text.primary" }}
                    >
                      {t.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem", lineHeight: 1.3 }}
                    >
                      {t.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Preview */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              bgcolor: "#e0e0e0",
              p: 3,
            }}
          >
            <Paper
              elevation={3}
              sx={{ maxWidth: 800, mx: "auto", overflow: "hidden" }}
            >
              <TemplateComponent quiz={quiz} showAnswers={showAnswers} school={school} />
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Print portal: renders outside #root so @media print can isolate it */}
      {open &&
        ReactDOM.createPortal(
          <div id="__qpm_print_portal__" style={{ display: "none" }}>
            <TemplateComponent quiz={quiz} showAnswers={showAnswers} school={school} />
          </div>,
          document.body
        )}
    </>
  );
};

export default PrintQuizModal;
